import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { useMediaQuery } from '@cocoonspace/shared/hooks/use-media-query.hook'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { fr } from 'date-fns/locale'
import { LogIn, LogOut, type LucideIcon } from 'lucide-react'
import { Trans, useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { Calendar, type CalendarProps } from '~/components/ui/calendar'
import { Card } from '~/components/ui/card'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '~/components/ui/select'
import { DayPicker } from '~/domains/search/components/filters/timeslot-filter/day-picker'
import { cn } from '~/lib/utils'
import { twTheme } from '~/theme'
import { type Timeslot, getDayValueAsArray } from './index'

const locales = {
	fr,
}

const styles: CalendarProps['classNames'] = {
	months:
		'md:chop-x chop-slate-200 chop-x-offset-5 grid grid-cols-1 gap-10 md:grid-cols-2',
}

export const TimePicker = ({
	value,
	Icon,
	onChange,
}: {
	value?: string
	Icon: LucideIcon
	onChange: (value: string) => void
}) => {
	const options = useMemo(() => {
		return searchConfig.timeSlots.filter((timeSlot) => {
			return (
				dateTimeUtils.utils.getTimeAsMinutes(searchConfig.timeSlot.min) <=
					dateTimeUtils.utils.getTimeAsMinutes(timeSlot) &&
				dateTimeUtils.utils.getTimeAsMinutes(searchConfig.timeSlot.max) >=
					dateTimeUtils.utils.getTimeAsMinutes(timeSlot)
			)
		})
	}, [])

	return (
		<Select
			value={value}
			onValueChange={onChange}
		>
			<SelectTrigger className='gap-2'>
				<Icon
					size={16}
					className='shrink-0 text-primary'
				/>

				<SelectValue />
			</SelectTrigger>

			<SelectContent className='w-[80px]'>
				{options.map((option) => (
					<SelectItem
						key={option}
						value={option}
					>
						{option}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

const defaultTimeDiff = 60

export const getEndTime = (newStart: string, value: Timeslot) => {
	if (!value.start && !value.end)
		return dateTimeUtils.utils.addDurationToTime(
			newStart,
			defaultTimeDiff,
			'minute',
		)

	const valueTimeDiff =
		(!!value.start &&
			!!value.end &&
			dateTimeUtils.utils
				.parseTime(value.end)
				.diff(dateTimeUtils.utils.parseTime(value.start), 'minute')) ||
		defaultTimeDiff

	const newEndTime =
		!!value.end && newStart < value.end
			? value.end
			: dateTimeUtils.utils.addDurationToTime(newStart, valueTimeDiff, 'minute')

	// * return the new end time if it's between the min and max time slot, else return the max end time
	return newEndTime > searchConfig.timeSlot.min &&
		newEndTime < searchConfig.timeSlots[searchConfig.timeSlots.length - 1]
		? newEndTime
		: searchConfig.timeSlot.max
}

export const getStartTime = (newEnd: string, value: Timeslot) =>
	!!value.start && newEnd <= value.start ? '' : value.start

export const SingleDate = ({
	className,
	value,
	onChange,
}: {
	isComplete: boolean
	className?: string
	value: Timeslot
	onChange: (value: Timeslot) => void
}) => {
	const { t } = useTranslation()

	return (
		<div
			className={cn(
				'chop-slate-200 chop-x-offset-5 chop-y-offset-5 max-md:chop-y md:chop-x grid grid-cols-1 gap-10 md:grid-cols-2',
				className,
			)}
		>
			<DayPicker
				className='w-full [&_.rdp-table]:mx-auto'
				value={value.day ? getDayValueAsArray(value.day)?.[0] : undefined}
				onChange={(day) => onChange({ ...value, day: [day] })}
			/>

			<div className='flex flex-col'>
				<div className='mb-4 text-center'>
					{t('search:dateFilter.timeTitle')}
				</div>

				<div className='flex flex-row items-center justify-center gap-8 md:flex-col md:gap-4'>
					<TimePicker
						Icon={LogIn}
						value={value.start || undefined}
						onChange={(start) => {
							const newEndTime = getEndTime(start, value)

							onChange({
								...value,
								start,
								end: newEndTime,
							})
						}}
					/>

					<TimePicker
						Icon={LogOut}
						value={value.end || undefined}
						onChange={(end) => {
							const newStartTime = getStartTime(end, value)

							onChange({
								...value,
								start: newStartTime,
								end,
							})
						}}
					/>
				</div>

				<Card className='mt-6 bg-slate-200/30 p-2 text-xs'>
					<Trans i18nKey='space:sidebar.monthSelect' />
				</Card>
			</div>
		</div>
	)
}

export const MultiDate = ({
	value,
	onChange,
	className,
}: {
	isComplete: boolean
	className?: string
	value: Timeslot
	onChange: (value: Timeslot) => void
}) => {
	const { i18n } = useTranslation()

	const isMobile = useMediaQuery(`(max-width: ${twTheme.theme.screens.md})`)

	return (
		<Calendar
			locale={locales[(i18n.language as 'fr') || 'fr']}
			mode='multiple'
			numberOfMonths={isMobile ? 1 : 2}
			fromDate={new Date()}
			toDate={dateTimeUtils().add(1, 'year').toDate()}
			selected={
				value.day
					? getDayValueAsArray(value.day)?.map((d) => dateTimeUtils(d).toDate())
					: undefined
			}
			className={cn('w-full [&_.rdp-table]:mx-auto', className)}
			classNames={styles}
			onSelect={(days) => {
				const day = days?.map((day) =>
					dateTimeUtils(day).customFormat('dateTech'),
				)

				onChange({
					day: day || null,
					start: null,
					end: null,
				})
			}}
		/>
	)
}
